/**
 * MARCA | 18/05/2026 | Bundle builder web component.
 * Manages product slot progress (fills with product images), collection tab
 * switching, product search, bundle selection state, and cart submission.
 * MARCA | 20/05/2026 | Updated for desktop vertical card layout, sidebar pricing panel,
 * and improved image sizing for responsive grid.
 * MARCA | 24/08/2026 | Optional PDP mode via data-initial-limit: first-page fetch + view more.
 * Missing attributes keep the standalone page behaviour unchanged.
 */
if (!customElements.get('bundle-builder')) {
  customElements.define(
    "bundle-builder",
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
        this.bundle = [];
        this.products = {};
        this.filteredProducts = [];
        this.activeTab = null;
        this.allProducts = [];
        this.isLoading = false;
      }

      connectedCallback() {
        this.milestones = JSON.parse(this.dataset.milestones || "[]");
        this.collections = JSON.parse(this.dataset.collections || "[]");
        this.currencySymbol = this.dataset.currency || "£";
        /* MARCA | 29/05/2026 | Bundle is now capped at a configurable maximum (default 5). The slot
           count equals this max — no extra/empty trailing slots, and the row no longer scrolls. */
        this.maxProducts = parseInt(this.dataset.maxProducts, 10) || 5;
        /* MARCA | 24/08/2026 | PDP first-load limit. 0 (the page default) shows the full collection. */
        this.initialLimit = parseInt(this.dataset.initialLimit, 10) || 0;
        this.previewSection =
          this.dataset.previewSection || "bundle-builder-collection-preview";
        this.collectionExpanded = {};
        this.hasMore = {};

        /* MARCA | 29/05/2026 | Slots + prices now render in two places (header + mobile bottom bar),
           so collect every matching target. The header slots row is kept separately for desktop sizing. */
        this.slotsContainers = this.querySelectorAll("[data-slots]");
        this.slotsContainer =
          this.querySelector(".bundle-builder__header [data-slots]") ||
          this.slotsContainers[0];
        this.productsContainer = this.querySelector("[data-products]");
        this.searchInput = this.querySelector("[data-search]");
        this.tabsContainer = this.querySelector("[data-tabs]");
        this.normalPriceEls = this.querySelectorAll("[data-normal-price]");
        this.summaryPriceEls = this.querySelectorAll("[data-summary-price]");
        this.discountsEl = this.querySelector("[data-discounts-value]");
        this.checkoutBtn = this.querySelector("[data-checkout]");
        this.headerEl = this.querySelector(".bundle-builder__header");
        this.mobileBar = this.querySelector("[data-mobilebar]");
        /* MARCA | 24/08/2026 | Present only on the PDP-embedded builder */
        this.viewMoreBtn = this.querySelector("[data-view-more]");

        this.bindEvents();
        this.renderSlots();
        this.updateSummary();
        /* MARCA | 28/05/2026 | Re-size slots once layout is available and on viewport resize */
        requestAnimationFrame(() => this.updateSlotSize());
        this._onResize = this.debounce(() => this.updateSlotSize(), 150);
        window.addEventListener("resize", this._onResize);
        /* MARCA | 29/05/2026 | Reveal the mobile sticky bar whenever the header scrolls out of view */
        this.setupMobileBar();
        this.loadInitialCollection();
      }

      disconnectedCallback() {
        if (this._onResize)
          window.removeEventListener("resize", this._onResize);
        if (this._headerObserver) this._headerObserver.disconnect();
      }

      bindEvents() {
        if (this.searchInput) {
          this.searchInput.addEventListener(
            "input",
            this.debounce(this.handleSearch.bind(this), 300),
          );
        }

        if (this.tabsContainer) {
          this.tabsContainer.addEventListener("click", (e) => {
            const tab = e.target.closest("[data-tab]");
            if (tab) this.switchTab(tab.dataset.tab);
          });
        }

        if (this.checkoutBtn) {
          this.checkoutBtn.addEventListener("click", () => this.submitBundle());
        }

        /* MARCA | 24/08/2026 | View more fetches the rest of the active collection on the PDP */
        if (this.viewMoreBtn) {
          this.viewMoreBtn.addEventListener("click", () =>
            this.loadRemainingProducts(),
          );
        }

        /* MARCA | 01/06/2026 | Mobile sidebar accordion — toggle Normal Price + Discounts visibility */
        const sidebarToggle = this.querySelector("[data-sidebar-toggle]");
        const sidebarDetails = this.querySelector("[data-sidebar-details]");
        if (sidebarToggle && sidebarDetails) {
          sidebarToggle.addEventListener("click", () => {
            const expanded =
              sidebarToggle.getAttribute("aria-expanded") === "true";
            sidebarToggle.setAttribute("aria-expanded", String(!expanded));
            sidebarDetails.classList.toggle("is-open", !expanded);
          });
        }

        /* MARCA | 29/05/2026 | Delegated remove handler — slot remove buttons now exist in both the
           header and the mobile bar, so listen on the component root instead of binding per-slot. */
        this.addEventListener("click", (e) => {
          const removeBtn = e.target.closest("[data-remove-index]");
          if (removeBtn) {
            e.preventDefault();
            this.removeFromBundle(parseInt(removeBtn.dataset.removeIndex, 10));
          }
        });
      }

      async loadInitialCollection() {
        if (this.collections.length === 0) return;
        const firstTab = this.tabsContainer?.querySelector("[data-tab]");
        if (firstTab) {
          this.switchTab(firstTab.dataset.tab);
        }
      }

      async switchTab(handle) {
        if (this.activeTab === handle && this.filteredProducts.length > 0)
          return;
        this.activeTab = handle;

        this.tabsContainer.querySelectorAll("[data-tab]").forEach((tab) => {
          tab.classList.toggle(
            "bundle-builder__tab--active",
            tab.dataset.tab === handle,
          );
        });

        if (!this.products[handle]) {
          await this.fetchCollection(handle);
        }

        this.applyFilter();
      }

      /* MARCA | 28/05/2026 | Fetch via section rendering to include custom.inspired_product metafield */
      /* MARCA | 24/08/2026 | PDP first load uses the preview section (8 products). The page
         builder still requests bundle-builder-collection and receives the full list. */
      async fetchCollection(handle, options = {}) {
        const loadRemaining = options.loadRemaining === true;
        const silent = options.silent === true;
        const usePreview =
          this.initialLimit > 0 &&
          !loadRemaining &&
          !this.collectionExpanded[handle];
        const sectionName = usePreview
          ? this.previewSection
          : "bundle-builder-collection";

        if (!silent) this.showLoading();
        try {
          const response = await fetch(
            `/collections/${handle}?sections=${sectionName}`,
          );
          if (!response.ok) throw new Error("Failed to load collection");
          const data = await response.json();
          const html = data[sectionName];
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const jsonStr = doc.body.textContent.trim();
          const parsed = JSON.parse(jsonStr);
          const products = Array.isArray(parsed)
            ? parsed
            : parsed.products || [];
          const hasMore = !Array.isArray(parsed) && parsed.hasMore === true;

          this.products[handle] = products.map((p) =>
            this.normalizeProduct(p, handle),
          );

          if (loadRemaining) {
            this.collectionExpanded[handle] = true;
            this.hasMore[handle] = false;
          } else if (usePreview) {
            this.hasMore[handle] = hasMore;
          } else {
            this.hasMore[handle] = false;
          }

          this.rebuildAllProducts();
        } catch (err) {
          console.error(
            `[BundleBuilder] Error loading collection "${handle}":`,
            err,
          );
          this.products[handle] = [];
          this.hasMore[handle] = false;
        } finally {
          if (!silent) this.hideLoading();
        }
      }

      /* MARCA | 24/08/2026 | Fetch the remainder of the active tab without replacing the grid with a spinner */
      async loadRemainingProducts() {
        if (!this.activeTab || this.collectionExpanded[this.activeTab]) return;

        if (this.viewMoreBtn) {
          this.viewMoreBtn.classList.add("loading");
          this.viewMoreBtn.disabled = true;
        }

        try {
          await this.fetchCollection(this.activeTab, {
            loadRemaining: true,
            silent: true,
          });
          this.applyFilter();
        } finally {
          if (this.viewMoreBtn) {
            this.viewMoreBtn.classList.remove("loading");
            this.viewMoreBtn.disabled = false;
          }
        }
      }

      /* MARCA | 24/08/2026 | Limit the visible grid until the customer asks for the rest */
      shouldLimitGrid() {
        if (!(this.initialLimit > 0)) return false;
        if ((this.searchInput?.value || "").trim()) return false;
        if (this.activeTab && this.collectionExpanded[this.activeTab])
          return false;
        return true;
      }

      updateViewMore() {
        if (!this.viewMoreBtn) return;

        const searching = (this.searchInput?.value || "").trim().length > 0;
        const show =
          this.initialLimit > 0 &&
          !searching &&
          Boolean(this.activeTab) &&
          this.hasMore[this.activeTab] === true &&
          !this.collectionExpanded[this.activeTab];

        this.viewMoreBtn.hidden = !show;
      }

      normalizeProduct(product, collectionHandle) {
        return {
          id: product.id,
          variantId: product.variantId,
          title: product.title,
          handle: product.handle,
          price: product.price || "0.00",
          image: product.image || "",
          inspiredBy: product.inspiredBy || "",
          vendor: product.vendor || "",
          collection: collectionHandle,
        };
      }

      rebuildAllProducts() {
        this.allProducts = [];
        Object.values(this.products).forEach((arr) => {
          arr.forEach((p) => {
            if (!this.allProducts.find((x) => x.id === p.id)) {
              this.allProducts.push(p);
            }
          });
        });
      }

      applyFilter() {
        const query = (this.searchInput?.value || "").toLowerCase().trim();

        if (query.length > 0) {
          this.filteredProducts = this.allProducts.filter(
            (p) =>
              p.title.toLowerCase().includes(query) ||
              p.inspiredBy.toLowerCase().includes(query) ||
              p.vendor.toLowerCase().includes(query),
          );
        } else if (this.activeTab && this.products[this.activeTab]) {
          this.filteredProducts = this.products[this.activeTab];
        } else {
          this.filteredProducts = this.allProducts;
        }

        this.renderProducts();
      }

      handleSearch() {
        const query = (this.searchInput?.value || "").trim();
        if (query.length > 0) {
          this.tabsContainer.querySelectorAll("[data-tab]").forEach((tab) => {
            tab.classList.remove("bundle-builder__tab--active");
          });
        } else if (this.activeTab) {
          this.tabsContainer.querySelectorAll("[data-tab]").forEach((tab) => {
            tab.classList.toggle(
              "bundle-builder__tab--active",
              tab.dataset.tab === this.activeTab,
            );
          });
        }
        this.applyFilter();
      }

      /* --- Slot rendering --- */
      /* MARCA | 28/05/2026 | Number overlaid in bottom-right of box, product title shown below */
      renderSlots() {
        if (!this.slotsContainers || this.slotsContainers.length === 0) return;

        /* MARCA | 29/05/2026 | Exactly maxProducts slots — no extra empty slots, no scrolling. */
        const totalSlots = this.maxProducts;

        let html = "";
        for (let i = 0; i < totalSlots; i++) {
          const slotNum = i + 1;
          const product = this.bundle[i] || null;
          const isNext = !product && i === this.bundle.length;
          const milestone = this.milestones.find(
            (m) => m.threshold === slotNum,
          );

          let slotClass = "bundle-builder__slot";
          if (product) slotClass += " bundle-builder__slot--filled";
          if (isNext) slotClass += " bundle-builder__slot--next";

          let boxContent = `<span class="bundle-builder__slot-number">${slotNum}</span>`;
          let belowBoxHtml = "";

          if (product && product.image) {
            boxContent += `
              <img src="${this.resizeImage(product.image, "180x180")}" alt="${this.escapeHtml(product.title)}" width="145" height="145">
              <button type="button" class="bundle-builder__slot-remove" data-remove-index="${i}" aria-label="Remove ${this.escapeHtml(product.title)}">×</button>
            `;
            belowBoxHtml = `<span class="bundle-builder__slot-title">${this.escapeHtml(product.title)}</span>`;
          } else {
            const labelHtml = milestone
              ? `<span class="bundle-builder__slot-label">${this.escapeHtml(milestone.label)}</span>`
              : "";
            boxContent += `
              ${labelHtml}
              <svg class="bundle-builder__slot-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"${milestone ? ' style="display:none"' : ""}>
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            `;
          }

          html += `
            <div class="${slotClass}">
              <div class="bundle-builder__slot-box">${boxContent}</div>
              ${belowBoxHtml}
            </div>
          `;
        }

        /* MARCA | 29/05/2026 | Render identical slot markup into every container (header + mobile bar).
           Remove buttons are handled by a delegated listener bound in bindEvents(). */
        this.slotsContainers.forEach((container) => {
          container.innerHTML = html;
        });
        this.updateSlotSize();
      }

      /* MARCA | 29/05/2026 | Size the header slots so all maxProducts slots fit the available width
         exactly (no scrolling). Capped on desktop so the squares don't become oversized; the mobile
         bottom-bar slots keep their own fixed CSS size. */
      updateSlotSize() {
        if (!this.slotsContainer) return;

        const available = this.slotsContainer.clientWidth;
        if (!available) return;

        const styles = getComputedStyle(this.slotsContainer);
        const gap = parseFloat(styles.columnGap || styles.gap) || 0;
        const n = Math.max(this.maxProducts, 1);
        const size = (available - (n - 1) * gap) / n;
        const max = window.matchMedia("(min-width: 990px)").matches
          ? 160
          : Infinity;
        const clamped = Math.max(0, Math.min(size, max));
        this.slotsContainer.style.setProperty("--bb-slot-size", `${clamped}px`);
      }

      /* MARCA | 29/05/2026 | Show the mobile sticky bar only while the header is out of view. Uses an
         IntersectionObserver (no scroll-handler jank); CSS restricts the bar itself to mobile widths. */
      setupMobileBar() {
        if (
          !this.mobileBar ||
          !this.headerEl ||
          !("IntersectionObserver" in window)
        )
          return;

        this._headerObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.mobileBar.classList.toggle(
                "is-visible",
                !entry.isIntersecting,
              );
              this.mobileBar.setAttribute(
                "aria-hidden",
                entry.isIntersecting ? "true" : "false",
              );
            });
          },
          { threshold: 0 },
        );
        this._headerObserver.observe(this.headerEl);

        /* MARCA | 01/06/2026 | Tapping anywhere on the mobile bar scrolls back to the header */
        this.mobileBar.addEventListener("click", () => {
          this.headerEl.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }

      isMobile() {
        return window.matchMedia("(max-width: 749px)").matches;
      }

      removeFromBundle(index) {
        if (index >= 0 && index < this.bundle.length) {
          this.bundle.splice(index, 1);
          this.updateUI();
        }
      }

      renderProducts() {
        if (!this.productsContainer) return;

        /* MARCA | 24/08/2026 | PDP shows only the first page until View more expands the tab */
        const productsToRender = this.shouldLimitGrid()
          ? this.filteredProducts.slice(0, this.initialLimit)
          : this.filteredProducts;

        if (productsToRender.length === 0) {
          this.productsContainer.innerHTML =
            '<div class="bundle-builder__empty">No products found.</div>';
          this.updateViewMore();
          return;
        }

        const fragment = document.createDocumentFragment();

        productsToRender.forEach((product) => {
          /* MARCA | 03/06/2026 | Count how many of this variant are in the bundle (supports duplicates) */
          const qty = this.bundle.filter(
            (b) => b.variantId === product.variantId,
          ).length;
          const card = this.createCard(product, qty);
          fragment.appendChild(card);
        });

        this.productsContainer.innerHTML = "";
        this.productsContainer.appendChild(fragment);
        this.refreshLooxWidgets();
        this.updateViewMore();
      }

      /* MARCA | 03/06/2026 | Card now shows a quantity selector when qty > 0 instead of a simple
         Add/Remove toggle, allowing customers to buy multiples of the same fragrance. */
      createCard(product, qty) {
        const card = document.createElement("div");
        card.className = "bundle-builder__card";
        card.dataset.variantId = product.variantId;

        const collectionLabel = this.getCollectionLabel(product.collection);
        const collectionColor = this.getCollectionColor(product.collection);
        const priceFormatted = this.formatMoney(product.price);
        const isFull = this.bundle.length >= this.maxProducts;
        const lockAdd = isFull && qty === 0;
        const imageHtml = product.image
          ? `<img src="${this.resizeImage(product.image, "400x400")}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="400" height="400">`
          : '<div style="width:100%;height:100%;background:#333;"></div>';

        let actionHtml;
        if (qty > 0) {
          const canIncrement = this.bundle.length < this.maxProducts;
          actionHtml = `
            <div class="bundle-builder__card-qty">
              <button type="button" class="bundle-builder__card-qty-btn" data-qty-action="decrement" aria-label="Remove one">−</button>
              <span class="bundle-builder__card-qty-value">${qty}</span>
              <button type="button" class="bundle-builder__card-qty-btn" data-qty-action="increment" aria-label="Add one"${!canIncrement ? " disabled" : ""}>+</button>
            </div>
          `;
        } else {
          actionHtml = `
            <button type="button" class="bundle-builder__card-add${lockAdd ? " bundle-builder__card-add--locked" : ""}" data-add-variant="${product.variantId}"${lockAdd ? " disabled" : ""}>
              ${lockAdd ? "Bundle Full" : "Add To Bundle"}
            </button>
          `;
        }

        card.innerHTML = `
          <div class="bundle-builder__card-image">${imageHtml}</div>
          <div class="bundle-builder__card-body">
            <div class="loox-rating" data-id="${product.id}" data-rating="" data-raters=""></div>
            <h3 class="bundle-builder__card-title">${this.escapeHtml(product.title)}</h3>
            <p class="bundle-builder__card-subtitle">${this.escapeHtml(product.inspiredBy || "")}</p>
            <p class="bundle-builder__card-price">${priceFormatted}</p>
            ${actionHtml}
          </div>
          ${collectionLabel ? `<span class="bundle-builder__card-badge" style="background:${collectionColor}">${this.escapeHtml(collectionLabel)}</span>` : ""}
        `;

        const addBtn = card.querySelector("[data-add-variant]");
        if (addBtn) {
          addBtn.addEventListener("click", (e) => {
            e.preventDefault();
            this.addProduct(product);
          });
        }

        const qtyBtns = card.querySelectorAll("[data-qty-action]");
        qtyBtns.forEach((btn) => {
          btn.addEventListener("click", (e) => {
            e.preventDefault();
            if (btn.dataset.qtyAction === "increment") {
              this.addProduct(product);
            } else {
              this.removeProduct(product);
            }
          });
        });

        return card;
      }

      /* MARCA | 03/06/2026 | Add one instance of a product to the bundle (each occupies its own slot) */
      addProduct(product) {
        if (this.bundle.length >= this.maxProducts) return;
        this.bundle.push(product);
        this.updateUI();

        if (this.isMobile() && this.headerEl) {
          this.headerEl.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }

      /* MARCA | 03/06/2026 | Remove one instance of a product from the bundle */
      removeProduct(product) {
        const idx = this.bundle.findIndex(
          (b) => b.variantId === product.variantId,
        );
        if (idx > -1) {
          this.bundle.splice(idx, 1);
          this.updateUI();
        }
      }

      toggleProduct(product) {
        const idx = this.bundle.findIndex(
          (b) => b.variantId === product.variantId,
        );
        const adding = idx === -1;
        if (idx > -1) {
          this.bundle.splice(idx, 1);
        } else {
          if (this.bundle.length >= this.maxProducts) return;
          this.bundle.push(product);
        }
        this.updateUI();

        if (adding && this.isMobile() && this.headerEl) {
          this.headerEl.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }

      updateUI() {
        this.renderSlots();
        this.updateSummary();
        this.updateCheckout();
        this.renderProducts();
      }

      /* MARCA | 28/05/2026 | Sidebar reflects the milestone tiers reached:
         - Normal price = bundle subtotal + value of any free gifts (gifts raise the "normal" price
           because the customer receives extra product at no cost).
         - Bundle price = subtotal minus the active bundle discount (percentage/fixed/cheapest-free).
         - Discounts row lists every perk reached as text (+ a saving amount where there is one).
         MARCA | 01/07/2026 | All totals use integer cents with per-line discount rounding to match
         Shopify's line-item percentage allocation (avoids subtotal-level float drift). */
      updateSummary() {
        let subtotalCents = 0;
        this.bundle.forEach((item) => {
          subtotalCents += this.priceToCents(item.price);
        });

        const { lines, priceDiscountCents, giftTotalCents } =
          this.calculateDiscounts(subtotalCents);
        const normalPriceCents = subtotalCents + giftTotalCents;
        const bundleTotalCents = Math.max(
          0,
          subtotalCents - priceDiscountCents,
        );

        /* MARCA | 29/05/2026 | Update every price target (header sidebar + mobile bottom bar) */
        const normalFormatted = this.formatCents(normalPriceCents);
        const bundleFormatted = this.formatCents(bundleTotalCents);
        this.normalPriceEls.forEach((el) => {
          el.textContent = normalFormatted;
        });
        this.summaryPriceEls.forEach((el) => {
          el.textContent = bundleFormatted;
        });
        this.renderDiscounts(lines);
      }

      /* MARCA | 28/05/2026 | Build the discount breakdown for the bundle. Percentage/fixed tiers are
         mutually exclusive (Shopify applies one bundle discount, so only the highest threshold reached
         counts). Free shipping (perk) and free gift tiers stack on top as separate perks. Returns the
         display lines plus the totals used for the normal/bundle prices.
         MARCA | 01/07/2026 | Percentage discounts are summed per line item (floor to cent), matching
         Shopify Function allocation_method "each". */
      calculateDiscounts(subtotalCents) {
        const lines = [];
        let priceDiscountCents = 0;
        let giftTotalCents = 0;

        const count = this.bundle.length;
        if (count === 0) return { lines, priceDiscountCents, giftTotalCents };

        const reached = (this.milestones || []).filter(
          (m) => m.threshold <= count,
        );

        const activeTier = reached
          .filter(
            (m) =>
              m.discountType === "percentage" || m.discountType === "fixed",
          )
          .sort((a, b) => b.threshold - a.threshold)[0];

        reached
          .slice()
          .sort((a, b) => a.threshold - b.threshold)
          .forEach((m) => {
            switch (m.discountType) {
              case "percentage":
              case "fixed": {
                if (m !== activeTier) return;
                const amountCents =
                  m.discountType === "percentage"
                    ? this.calculatePercentageDiscountCents(
                        parseFloat(m.discountValue) || 0,
                      )
                    : Math.min(
                        this.priceToCents(m.discountValue),
                        subtotalCents,
                      );
                priceDiscountCents += amountCents;
                lines.push({
                  label: m.label,
                  amount: `−${this.formatCents(amountCents)}`,
                });
                break;
              }
              case "cheapest_free": {
                const pricesCents = this.bundle.map((b) =>
                  this.priceToCents(b.price),
                );
                const amountCents = pricesCents.length
                  ? Math.min(...pricesCents)
                  : 0;
                priceDiscountCents += amountCents;
                lines.push({
                  label: m.label,
                  amount: `−${this.formatCents(amountCents)}`,
                });
                break;
              }
              case "free_gift": {
                const giftCents = this.priceToCents(m.giftPrice);
                giftTotalCents += giftCents;
                lines.push({
                  label: m.label,
                  amount:
                    giftCents > 0 ? `−${this.formatCents(giftCents)}` : "",
                });
                break;
              }
              default: {
                lines.push({ label: m.label, amount: "" });
              }
            }
          });

        return { lines, priceDiscountCents, giftTotalCents };
      }

      /* MARCA | 01/07/2026 | Mirror Shopify per-line percentage discount rounding (floor to cent). */
      calculatePercentageDiscountCents(percentage) {
        let totalCents = 0;
        this.bundle.forEach((item) => {
          const priceCents = this.priceToCents(item.price);
          totalCents += Math.floor((priceCents * percentage) / 100);
        });
        return totalCents;
      }

      /* MARCA | 28/05/2026 | Render the discounts list (perk text + optional saving amount) */
      renderDiscounts(lines) {
        if (!this.discountsEl) return;

        if (!lines || lines.length === 0) {
          this.discountsEl.innerHTML =
            '<span class="bundle-builder__discount-empty">—</span>';
          return;
        }

        this.discountsEl.innerHTML = lines
          .map(
            (line) => `
            <span class="bundle-builder__discount-line">
              <span class="bundle-builder__discount-name">${this.escapeHtml(line.label)}</span>
              ${line.amount ? `<span class="bundle-builder__discount-amount">${this.escapeHtml(line.amount)}</span>` : ""}
            </span>`,
          )
          .join("");
      }

      updateCheckout() {
        if (!this.checkoutBtn) return;
        this.checkoutBtn.disabled = this.bundle.length === 0;
      }

      async submitBundle() {
        if (this.bundle.length === 0) return;

        this.checkoutBtn.classList.add("loading");
        this.checkoutBtn.disabled = true;

        /* MARCA | 03/06/2026 | Stack duplicate variants into a single line item with the summed
           quantity so the cart groups them as one (e.g. 3x same fragrance = qty 3). */
        const qtyMap = {};
        this.bundle.forEach((item) => {
          qtyMap[item.variantId] = (qtyMap[item.variantId] || 0) + 1;
        });
        /* MARCA | 01/07/2026 | 10% bundle discount is applied by a Shopify Function that reads
           the _bb_discount_10 line item property — only set when 3+ bottles are in the bundle. */
        const applyBundleDiscount = this.bundle.length >= 3;
        const items = Object.entries(qtyMap).map(([id, quantity]) => {
          const item = { id: Number(id), quantity };
          if (applyBundleDiscount) {
            item.properties = { _bb_discount_10: "true" };
          }
          return item;
        });

        try {
          const response = await fetch(
            window.theme?.routes?.cart_add_url || "/cart/add.js",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/javascript",
              },
              body: JSON.stringify({ items }),
            },
          );

          const data = await response.json();

          if (data.status && data.status !== 200) {
            console.error(
              "[BundleBuilder] Cart error:",
              data.description || data.message,
            );
            return;
          }

          /* MARCA | 28/05/2026 | Open cart drawer after adding bundle items */
          const miniCart = document.querySelector("mini-cart");
          if (miniCart) {
            const sectionsUrl = `${window.location.pathname}?sections=mini-cart,cart-icon-bubble,mobile-cart-icon-bubble`;
            const sectionsResponse = await fetch(sectionsUrl);
            const sections = await sectionsResponse.json();
            miniCart.renderContents({ id: data.items?.[0]?.id, sections });
          } else {
            window.location.href = window.theme?.routes?.cart_url || "/cart";
          }
        } catch (err) {
          console.error("[BundleBuilder] Submit error:", err);
        } finally {
          this.checkoutBtn.classList.remove("loading");
          this.checkoutBtn.disabled = false;
        }
      }

      /* --- Loox integration --- */
      refreshLooxWidgets() {
        try {
          if (window.loox && typeof window.loox.refresh === "function") {
            window.loox.refresh();
          } else if (window.Loox && typeof window.Loox.init === "function") {
            window.Loox.init();
          } else {
            document.dispatchEvent(new CustomEvent("loox:domChanged"));
          }
        } catch (e) {}
      }

      /* --- Utilities --- */
      showLoading() {
        this.isLoading = true;
        if (this.productsContainer) {
          this.productsContainer.innerHTML =
            '<div class="bundle-builder__loading"><span class="bundle-builder__loading-spinner"></span></div>';
        }
      }

      hideLoading() {
        this.isLoading = false;
      }

      getCollectionLabel(handle) {
        const col = this.collections.find((c) => c.handle === handle);
        return col?.label || "";
      }

      getCollectionColor(handle) {
        const col = this.collections.find((c) => c.handle === handle);
        return col?.color || "#555";
      }

      formatMoney(priceStr) {
        const amount = parseFloat(priceStr);
        if (isNaN(amount)) return `${this.currencySymbol}0.00`;
        return `${this.currencySymbol}${amount.toFixed(2)}`;
      }

      /* MARCA | 01/07/2026 | Convert decimal price strings to integer cents for accurate totals. */
      priceToCents(price) {
        return Math.round((parseFloat(price) || 0) * 100);
      }

      /* MARCA | 01/07/2026 | Format integer cents as a currency string. */
      formatCents(cents) {
        return this.formatMoney((cents / 100).toFixed(2));
      }

      resizeImage(url, size) {
        if (!url) return "";
        return url.replace(/(\.[^.]+)$/, `_${size}$1`);
      }

      escapeHtml(str) {
        const div = document.createElement("div");
        div.textContent = str;
        return div.innerHTML;
      }

      debounce(fn, wait) {
        let t;
        return (...args) => {
          clearTimeout(t);
          t = setTimeout(() => fn.apply(this, args), wait);
        };
      }
    },
  );
}
